import * as React from 'react';
import { AppContext } from '../../../App';
import { Box, TextField, Select, MenuItem, Button } from '@mui/material';
import useGlynt from '../../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { showPackagerCoCReportAtom } from '../../../atoms/packager-atoms';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import { saveAs } from 'file-saver';
import {
  OutputFormatTypes,
  ChainOfCustodyReportEntry,
  ChainOfCustodyReportResponse,
  ChainOfCustodyGeneratingResponse,
} from '../../../types/packager-types';
import PackagerChainOfCustodyReportTable from './PackagerChainOfCustodyReportTable';
import useGetPackager from '../../hooks/packager/useGetPackager';
import { useParams } from 'react-router';
import { btnSubtlePrimarySx } from '../../common/filterStyles';

interface FormData {
  start_date: string;
  end_date: string;
  format: OutputFormatTypes;
}

export interface IPackagerChainOfCustodyReportProps {
  packagerId: string;
}

const PackagerChainOfCustodyReport: React.FC<IPackagerChainOfCustodyReportProps> = ({
  packagerId,
}) => {
  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();
  const [showPackagerCoCReport, setShowPackagerCoCReport] = useAtom(showPackagerCoCReportAtom);
  const [reportData, setReportData] = React.useState<ChainOfCustodyReportEntry[]>([]);
  const [formatState, setFormatState] = React.useState<OutputFormatTypes>(OutputFormatTypes.JSON);
  const [isPolling, setIsPolling] = React.useState<boolean>(false);
  const retryTimeoutRef = React.useRef<number | null>(null);
  const retryCountRef = React.useRef<number>(0);

  const { dpid } = useParams<{ dpid: string }>();
  const { packager } = useGetPackager(dpid!);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid, dirtyFields },
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      start_date: packager?.coc_report_start_date || '',
      end_date: packager?.coc_report_end_date || '',
      format: OutputFormatTypes.JSON,
    },
    mode: 'onChange',
  });
  // Watch form values for auto-refresh and syncing
  const watchedValues = watch();
  const watchedFormat = useWatch({
    control,
    name: 'format',
  });
  // Keep form start/end date in sync when packager changes while modal is open
  React.useEffect(() => {
    if (!packager) return;
    // Update to latest server values unless the user has modified the fields
    const serverStart = packager.coc_report_start_date || '';
    const serverEnd = packager.coc_report_end_date || '';
    if (!dirtyFields.start_date && serverStart !== watchedValues.start_date) {
      setValue('start_date', serverStart, { shouldDirty: false, shouldValidate: false });
    }
    if (!dirtyFields.end_date && serverEnd !== watchedValues.end_date) {
      setValue('end_date', serverEnd, { shouldDirty: false, shouldValidate: false });
    }
  }, [
    packager,
    setValue,
    watchedValues.start_date,
    watchedValues.end_date,
    dirtyFields.start_date,
    dirtyFields.end_date,
  ]);

  // Update formatState when watchedFormat changes
  React.useEffect(() => {
    if (watchedFormat) {
      setFormatState(watchedFormat);
    }
  }, [watchedFormat]);

  const chainOfCustodyReportMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await (
        await glynt
      ).packager.chainOfCustodyReport(packagerId, {
        format: data.format.toLowerCase() as 'csv' | 'json',
        download: true,
        start_date: data.start_date,
        end_date: data.end_date,
      });

      return response;
    },

    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });

      // Handle different response formats
      if (variables.format === OutputFormatTypes.JSON) {
        const possibleGenerating = response as unknown as
          | ChainOfCustodyReportResponse
          | ChainOfCustodyGeneratingResponse;
        const interim = possibleGenerating as ChainOfCustodyGeneratingResponse;
        if (interim.status === 'failed') {
          setIsPolling(false);
          setToast({
            variant: 'error',
            open: true,
            message: interim.message || 'Failed to generate Chain of Custody report.',
          });
          return;
        }
        if (interim.status === 'success') {
          // Immediately refetch to get the final data
          retryCountRef.current = 0;
          chainOfCustodyReportMutation.mutate(variables);
          return;
        }
        if (interim.status === 'generating') {
          // Schedule retry if under max attempts
          retryCountRef.current += 1;
          if (retryCountRef.current <= 5) {
            setIsPolling(true);
            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }
            retryTimeoutRef.current = window.setTimeout(() => {
              chainOfCustodyReportMutation.mutate(variables);
            }, 3000);
            return;
          }
          setIsPolling(false);
          setToast({
            variant: 'error',
            open: true,
            message: 'Report is still generating. Please try again later.',
          });
          return;
        }

        // JSON response is wrapped in an envelope
        const jsonResponse = response as unknown as ChainOfCustodyReportResponse;
        const parsedData = Array.isArray(jsonResponse.data) ? jsonResponse.data : [];
        setReportData(parsedData);
        setIsPolling(false);
        setToast({
          variant: 'success',
          open: true,
          message: `Chain of Custody report loaded successfully. Found ${parsedData.length} records.`,
        });
      } else {
        // CSV response needs to be parsed
        const csvString = response as unknown as string;
        const lines = csvString.split('\n');
        const headers = lines[0].split(',');
        const parsedData = lines
          .slice(1)
          .filter((line) => line.trim())
          .map((line) => {
            const values = line.split(',');
            const row: any = {};
            headers.forEach((header, index) => {
              row[header.trim()] = values[index]?.trim() || '';
            });
            return row as ChainOfCustodyReportEntry;
          });
        setReportData(parsedData);
        setIsPolling(false);
        setToast({
          variant: 'success',
          open: true,
          message: `Chain of Custody report loaded successfully. Found ${parsedData.length} records.`,
        });
      }
    },

    onError: (error: AxiosError) => {
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });
      const errorMessage =
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
          ? (error.response.data as { message: string }).message
          : 'Sorry, something went wrong.';
      setToast({
        variant: 'error',
        open: true,
        message: errorMessage,
      });
    }
  });

  const handleClickOpen = () => {
    // Prefill immediately with the latest known packager values
    const serverStart = packager?.coc_report_start_date || '';
    const serverEnd = packager?.coc_report_end_date || '';
    setValue('start_date', serverStart, { shouldDirty: false, shouldValidate: false });
    setValue('end_date', serverEnd, { shouldDirty: false, shouldValidate: false });
    setShowPackagerCoCReport(true);
  };

  const handleCloseConfirmation = () => {
    setShowPackagerCoCReport(false);
    setReportData([]);
    setIsPolling(false);
    retryCountRef.current = 0;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  const onSubmit = (data: FormData) => {
    retryCountRef.current = 0;
    chainOfCustodyReportMutation.mutate(data);
  };

  const handleFormSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const handleDownload = () => {
    chainOfCustodyReportMutation.mutate(
      {
        start_date: watchedValues.start_date,
        end_date: watchedValues.end_date,
        format: formatState,
      },
      {
        onSuccess: (response) => {
          let content: string;
          const downloadFormat = formatState;

          if (downloadFormat === OutputFormatTypes.CSV) {
            // CSV response is already a string
            content = response as unknown as string;
          } else {
            // JSON response is an envelope, stringify as-is
            content = JSON.stringify(response, null, 2);
          }

          const blob = new Blob([content], {
            type:
              downloadFormat === OutputFormatTypes.CSV
                ? 'text/csv;charset=utf-8'
                : 'application/json;charset=utf-8',
          });
          saveAs(blob, `chain-of-custody-report.${downloadFormat.toLowerCase()}`);

          setToast({
            variant: 'success',
            open: true,
            message: 'Chain of Custody report downloaded successfully.',
          });
        },
        onError: (error: AxiosError) => {
          const errorMessage =
            error.response?.data &&
            typeof error.response.data === 'object' &&
            'message' in error.response.data
              ? (error.response.data as { message: string }).message
              : 'Sorry, something went wrong downloading the report.';
          setToast({
            variant: 'error',
            open: true,
            message: errorMessage,
          });
        },
      }
    );
  };

  // Auto-refresh data when form values change
  React.useEffect(() => {
    if (watchedValues.start_date && watchedValues.end_date && reportData.length > 0) {
      const timeoutId = setTimeout(() => {
        handleSubmit(onSubmit)();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [watchedValues.start_date, watchedValues.end_date, watchedValues.format]);

  // Cleanup any pending retry on unmount
  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClickOpen}
        id={`btn-packager-settings`}
        sx={btnSubtlePrimarySx}
      >
        CoC Report
      </Button>
      <DialogGlynt
        open={showPackagerCoCReport}
        closeModal={handleCloseConfirmation}
        loading={chainOfCustodyReportMutation.isPending || isPolling}
        cypressId="dialog-package-freeze"
        width={reportData.length > 0 && formatState === OutputFormatTypes.JSON ? '1400px' : '600px'}
      >
        <DialogTitleGlynt
          title="Chain of Custody Report"
          closeModal={handleCloseConfirmation}
          loading={chainOfCustodyReportMutation.isPending}
        />
        <DialogContentGlynt>
          <Box
            sx={{
              borderRadius: '4px',
              marginBottom: '10px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 2,
                justifyContent: 'flex-end',
              }}
            >
              <FormControlStyled
                sx={{
                  width: reportData.length > 0 ? '132px' : 'calc(33.33% - 8px)',
                }}
              >
                <FormLabelStyled>FORMAT</FormLabelStyled>
                <Controller
                  name="format"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      variant="outlined"
                      size="small"
                      data-cy="coc-report-format-select"
                      disabled={chainOfCustodyReportMutation.isPending || isPolling}
                    >
                      <MenuItem value={OutputFormatTypes.JSON}>JSON</MenuItem>
                      <MenuItem value={OutputFormatTypes.CSV}>CSV</MenuItem>
                    </Select>
                  )}
                />
              </FormControlStyled>

              <FormControlStyled
                sx={{
                  width: reportData.length > 0 ? '154px' : 'calc(33.33% - 8px)',
                }}
              >
                <FormLabelStyled>START DATE</FormLabelStyled>
                <Controller
                  name="start_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      variant="outlined"
                      size="small"
                      data-cy="coc-report-start-date-input"
                      disabled={chainOfCustodyReportMutation.isPending || isPolling}
                    />
                  )}
                />
              </FormControlStyled>

              <FormControlStyled
                sx={{
                  width: reportData.length > 0 ? '154px' : 'calc(33.33% - 8px)',
                }}
              >
                <FormLabelStyled>END DATE</FormLabelStyled>
                <Controller
                  name="end_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      variant="outlined"
                      size="small"
                      data-cy="coc-report-end-date-input"
                      disabled={chainOfCustodyReportMutation.isPending || isPolling}
                    />
                  )}
                />
              </FormControlStyled>
            </Box>
          </Box>

          {reportData.length > 0 && formatState === OutputFormatTypes.JSON && (
            <PackagerChainOfCustodyReportTable reportData={reportData} />
          )}
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleFormSubmit}
          closeModal={handleCloseConfirmation}
          clearButton={handleDownload}
          cancelCypressId="btn-coc-report-modal-cancel"
          submitCypressId="btn-coc-report-generate"
          clearCypressId="btn-coc-report-download"
          submitButtonText="View Results"
          clearButtonText="Download"
          submitLoading={chainOfCustodyReportMutation.isPending || isPolling}
          submitDisabled={formatState === OutputFormatTypes.CSV || isPolling}
          clearDisabled={chainOfCustodyReportMutation.isPending || isPolling}
          showClearButton={true}
          showCancelButton={true}
        />
      </DialogGlynt>
    </>
  );
};

export default PackagerChainOfCustodyReport;
